import type { GuidePhase, ReportGuideStepId } from './reportGuideSteps';
import { REPORT_GUIDE_STEPS, checklistKey, stepsForPhase, totalChecklistCount } from './reportGuideSteps';

const STORAGE_PREFIX = 'reportGuide.v1';

export interface ReportGuideProgress {
  lastStepId: ReportGuideStepId;
  checkedItems: string[];
  dismissedTips: string[];
  /**
   * ISO timestamp set once when the user first reaches 100% (gates the
   * one-shot completion celebration animation). Optional — old payloads
   * without it parse fine.
   */
  completionCelebratedAt?: string;
}

const DEFAULT_PROGRESS: ReportGuideProgress = {
  lastStepId: 'create-site',
  checkedItems: [],
  dismissedTips: [],
};

function storageKey(uid: string | null | undefined): string {
  return `${STORAGE_PREFIX}.${uid ?? 'anonymous'}`;
}

/** All currently-valid `${stepId}:${itemId}` keys. */
function validChecklistKeys(): Set<string> {
  return new Set(
    REPORT_GUIDE_STEPS.flatMap(s => s.checklist.map(item => checklistKey(s.id, item.id))),
  );
}

export function loadReportGuideProgress(uid: string | null | undefined): ReportGuideProgress {
  if (typeof window === 'undefined') return { ...DEFAULT_PROGRESS };
  try {
    const raw = localStorage.getItem(storageKey(uid));
    if (!raw) return { ...DEFAULT_PROGRESS };
    const parsed = JSON.parse(raw) as Partial<ReportGuideProgress>;
    const lastStepId = REPORT_GUIDE_STEPS.some(s => s.id === parsed.lastStepId)
      ? (parsed.lastStepId as ReportGuideStepId)
      : DEFAULT_PROGRESS.lastStepId;
    // Filter stored keys against the CURRENT registry — step/item renames must
    // not leave orphan keys inflating the progress percentage (> 100% risk).
    const valid = validChecklistKeys();
    return {
      lastStepId,
      checkedItems: Array.isArray(parsed.checkedItems)
        ? parsed.checkedItems.filter(x => typeof x === 'string' && valid.has(x))
        : [],
      dismissedTips: Array.isArray(parsed.dismissedTips)
        ? parsed.dismissedTips.filter(x => typeof x === 'string')
        : [],
      ...(typeof parsed.completionCelebratedAt === 'string'
        ? { completionCelebratedAt: parsed.completionCelebratedAt }
        : {}),
    };
  } catch {
    return { ...DEFAULT_PROGRESS };
  }
}

export function saveReportGuideProgress(uid: string | null | undefined, progress: ReportGuideProgress): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey(uid), JSON.stringify(progress));
  } catch { /* quota */ }
}

export function isChecklistItemChecked(
  progress: ReportGuideProgress,
  stepId: ReportGuideStepId,
  itemId: string,
): boolean {
  return progress.checkedItems.includes(checklistKey(stepId, itemId));
}

export function toggleChecklistItem(
  uid: string | null | undefined,
  progress: ReportGuideProgress,
  stepId: ReportGuideStepId,
  itemId: string,
): ReportGuideProgress {
  const key = checklistKey(stepId, itemId);
  const checked = new Set(progress.checkedItems);
  if (checked.has(key)) checked.delete(key);
  else checked.add(key);
  const next: ReportGuideProgress = {
    ...progress,
    lastStepId: stepId,
    checkedItems: [...checked],
  };
  saveReportGuideProgress(uid, next);
  return next;
}

export function setLastGuideStep(
  uid: string | null | undefined,
  progress: ReportGuideProgress,
  stepId: ReportGuideStepId,
): ReportGuideProgress {
  const next = { ...progress, lastStepId: stepId };
  saveReportGuideProgress(uid, next);
  return next;
}

export function dismissGuideTip(
  uid: string | null | undefined,
  progress: ReportGuideProgress,
  tipId: string,
): ReportGuideProgress {
  if (progress.dismissedTips.includes(tipId)) return progress;
  const next: ReportGuideProgress = {
    ...progress,
    dismissedTips: [...progress.dismissedTips, tipId],
  };
  saveReportGuideProgress(uid, next);
  return next;
}

export function isGuideTipDismissed(progress: ReportGuideProgress, tipId: string): boolean {
  return progress.dismissedTips.includes(tipId);
}

export function computeGuideProgressPercent(progress: ReportGuideProgress): number {
  const total = totalChecklistCount();
  if (total === 0) return 0;
  return Math.round((progress.checkedItems.length / total) * 100);
}

export function stepCompletionPercent(
  progress: ReportGuideProgress,
  stepId: ReportGuideStepId,
): number {
  const step = REPORT_GUIDE_STEPS.find(s => s.id === stepId);
  if (!step || step.checklist.length === 0) return 0;
  const done = step.checklist.filter(item =>
    isChecklistItemChecked(progress, stepId, item.id),
  ).length;
  return Math.round((done / step.checklist.length) * 100);
}

/** True when every checklist item of the step is checked (empty = not complete). */
export function isStepComplete(progress: ReportGuideProgress, stepId: ReportGuideStepId): boolean {
  const step = REPORT_GUIDE_STEPS.find(s => s.id === stepId);
  if (!step || step.checklist.length === 0) return false;
  return step.checklist.every(item => isChecklistItemChecked(progress, stepId, item.id));
}

/** Steps fully complete within a phase: { done, total }. */
export function phaseStepTally(
  progress: ReportGuideProgress,
  phase: GuidePhase,
): { done: number; total: number } {
  const steps = stepsForPhase(phase);
  const done = steps.filter(s => isStepComplete(progress, s.id)).length;
  return { done, total: steps.length };
}

/** Checklist-item completion percent within a phase (drives the meters). */
export function phaseCompletionPercent(
  progress: ReportGuideProgress,
  phase: GuidePhase,
): number {
  const steps = stepsForPhase(phase);
  const total = steps.reduce((n, s) => n + s.checklist.length, 0);
  if (total === 0) return 0;
  const done = steps.reduce(
    (n, s) => n + s.checklist.filter(item => isChecklistItemChecked(progress, s.id, item.id)).length,
    0,
  );
  return Math.round((done / total) * 100);
}

/** Count of fully complete steps across the whole guide. */
export function completedStepCount(progress: ReportGuideProgress): number {
  return REPORT_GUIDE_STEPS.filter(s => isStepComplete(progress, s.id)).length;
}

export function isGuideComplete(progress: ReportGuideProgress): boolean {
  return completedStepCount(progress) === REPORT_GUIDE_STEPS.length;
}

/** One-shot: stamp the celebration timestamp the first time 100% is reached. */
export function markGuideCompletionCelebrated(
  uid: string | null | undefined,
  progress: ReportGuideProgress,
): ReportGuideProgress {
  if (progress.completionCelebratedAt) return progress;
  const next: ReportGuideProgress = {
    ...progress,
    completionCelebratedAt: new Date().toISOString(),
  };
  saveReportGuideProgress(uid, next);
  return next;
}
